import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ChatCircleText, Spinner, Trash, CheckCircle, ArrowCounterClockwise, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDate = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export default function AdminContacts() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get("/admin/contact-messages").then(({ data }) => { setRows(data); setLoading(false); }).catch(() => setLoading(false)); };
  useEffect(load, []);
  if (user.role !== "super_admin") return <div className="p-8 text-zinc-500">Super admin access only.</div>;

  const setStatus = async (r, status) => {
    try { await api.post(`/admin/contact-messages/${r.id}/status`, { status }); setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, status } : x)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete the question from ${r.name}?`)) return;
    try { await api.delete(`/admin/contact-messages/${r.id}`); setRows((rs) => rs.filter((x) => x.id !== r.id)); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-contacts-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">Website Questions</h1>
        <p className="text-zinc-500 text-sm mt-1">{rows.length} total · {rows.filter((r) => r.status !== "replied").length} new</p>
      </div>
      <div className="rounded-lg border border-zinc-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-zinc-50"><TableHead>Received</TableHead><TableHead>From</TableHead><TableHead>Question</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16"><Spinner size={28} className="animate-spin text-emerald-600 mx-auto" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-16 text-zinc-400"><ChatCircleText size={32} className="mx-auto mb-2 text-zinc-300" />No questions yet.</TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.id} className={r.status === "replied" ? "opacity-70" : ""} data-testid={`contact-row-${r.id}`}>
                <TableCell className="text-xs text-zinc-500 whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                <TableCell className="text-sm">
                  <div className="font-medium">{r.name}{r.company ? <span className="text-zinc-400 font-normal"> · {r.company}</span> : null}</div>
                  <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-emerald-700 hover:underline text-xs mt-0.5"><EnvelopeSimple size={13} /> {r.email}</a>
                  {r.phone && <div className="text-xs text-zinc-400">{r.phone}</div>}
                </TableCell>
                <TableCell className="text-sm text-zinc-700 max-w-xl whitespace-pre-line">{r.message}</TableCell>
                <TableCell>{r.status === "replied" ? <Badge className="bg-zinc-200 text-zinc-700 hover:bg-zinc-200">Replied</Badge> : <Badge className="bg-emerald-500 text-white hover:bg-emerald-500">New</Badge>}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    {r.status === "replied"
                      ? <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-500" title="Reopen" onClick={() => setStatus(r, "new")} data-testid={`contact-reopen-${r.id}`}><ArrowCounterClockwise size={16} /></Button>
                      : <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-600" title="Mark replied" onClick={() => setStatus(r, "replied")} data-testid={`contact-replied-${r.id}`}><CheckCircle size={18} weight="fill" /></Button>}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" title="Delete" onClick={() => del(r)} data-testid={`contact-delete-${r.id}`}><Trash size={16} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
